import { isServer, MutationCache, QueryCache, QueryClient, type QueryKey } from "@tanstack/react-query";
import { unstable_isUnrecognizedActionError } from "next/navigation";
import { hashFn } from "wagmi/query";

import { dispatchAgoraEvent } from "@/lib/utils/dispatch-agora-event";
import { dispatchCustomEvent } from "@/lib/utils/dispatch-custom-event";
import { isClient } from "@/lib/utils/is-client";

type ProxyAuthError = {
  response: {
    data: {
      code?: string;
      details?: {
        response?: {
          code?: string;
        };
      };
      error: "PROXY_AUTH_FAILED" | "PROXY_AUTH_REQUIRED";
    };
    status: number;
  };
};

function makeQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error: unknown, query) => {
      if (!isClient()) return;

      const proxyError = error as ProxyAuthError;
      const errorData = proxyError?.response?.data;

      const eventDetails = {
        error_message: (error instanceof Error ? error.message : null) ?? "Unknown error_message",
        response_data_code: errorData?.code ?? "Unknown response_data_code",
        response_data_error: errorData?.error ?? "Unknown response_data_error",
        response_status: proxyError?.response?.status ?? "Unknown response_status",
        query_hash: query?.queryHash ?? "Unknown query_hash",
        query_key: query?.queryKey ? JSON.stringify(query.queryKey) : "Unknown query_key",
      };

      const isAuthRequired = errorData?.error === "PROXY_AUTH_REQUIRED";
      const isAuthFailed = errorData?.error === "PROXY_AUTH_FAILED";
      // Keyed on code (not status 401) so a backend path emitting 401 for a non-auth reason can't force a reauth loop.
      const isUpstreamUnauthorized = errorData?.details?.response?.code === "UNAUTHORIZED";

      if (isAuthRequired || isAuthFailed || isUpstreamUnauthorized) {
        // Distinct EventActions per trigger; upstream-unauthorized signals backend/proxy contract drift.
        const eventAction = isUpstreamUnauthorized
          ? "error.auth.upstream-unauthorized"
          : isAuthRequired
            ? "error.auth.required"
            : "error.auth.failed";

        dispatchAgoraEvent({
          eventAction,
          eventDetails,
          eventLocation: "global",
          eventType: "product.error.captured",
        });
        dispatchCustomEvent("__ausd_reauth-required");
        return;
      }

      dispatchAgoraEvent({
        eventLocation: "global",
        eventType: "product.error.captured",
        eventAction: "error.query-cache.unknown",
        eventDetails,
      });
    },
  });
}

// A page loaded before a deploy calls Server Actions the new build doesn't have; a reload picks up the new build.
function makeMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error) => {
      if (!isClient() || !unstable_isUnrecognizedActionError(error)) return;
      // Counts how often a deploy reloads someone mid-step, since the reload drops that step's unsaved edits.
      dispatchAgoraEvent({
        eventAction: "error.mutation-cache.stale-build",
        eventDetails: { page_path: window.location.pathname },
        eventLocation: "global",
        eventType: "product.error.captured",
      });
      window.location.reload();
    },
  });
}

const STALE_TIME_IN_MINUTES = 1;
const STALE_TIME_IN_MS = STALE_TIME_IN_MINUTES * 60 * 1000;

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // https://wagmi.sh/react/guides/tanstack-query#devtools
        queryKeyHashFn: hashFn,
        retry: 3,

        // https://tanstack.com/query/v5/docs/framework/react/guides/ssr
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: STALE_TIME_IN_MS,
      },
    },

    mutationCache: makeMutationCache(),
    queryCache: makeQueryCache(),
  });
}

let browserQueryClient: QueryClient | undefined;

export interface TryMutationCallbackProps<TResult = unknown> {
  onError?: (error: unknown) => void;
  onLoading?: () => void;
  onSuccess?: (result: TResult) => void;
}

export interface TryMutationProps<TResult = unknown> extends TryMutationCallbackProps<TResult> {
  onMutate: () => Promise<TResult> | TResult;
}

export function getQueryClient(): QueryClient {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export async function invalidateQueriesAndEnsureFreshData(queryKeys: QueryKey[]) {
  const client = getQueryClient();

  const filteredQueryKeys = queryKeys.filter((queryKey) => {
    const isQueryKeyInCache = client.getQueryState(queryKey);

    if (!isQueryKeyInCache) {
      return false;
    }

    return true;
  });

  // First invalidate all queries
  await Promise.all(filteredQueryKeys.map((queryKey) => client.invalidateQueries({ queryKey })));

  // Then ensure fresh data for all
  return Promise.all(filteredQueryKeys.map((queryKey) => client.ensureQueryData({ queryKey })));
}

export async function invalidateQueryAndEnsureFreshData(queryKey: QueryKey) {
  const client = getQueryClient();

  const isQueryKeyInCache = client.getQueryState(queryKey);

  if (!isQueryKeyInCache) {
    return;
  }

  await client.invalidateQueries({ queryKey });
  return client.ensureQueryData({ queryKey });
}

export function invalidateQueryCache() {
  const queryClient = getQueryClient();

  return queryClient.getQueryCache().clear();
}

export async function tryMutation<TResult>(props: TryMutationProps<TResult>): Promise<void> {
  const { onError, onLoading, onMutate, onSuccess } = props;

  try {
    if (onLoading) {
      onLoading();
    }

    const result = await onMutate();

    if (onSuccess) {
      onSuccess(result);
    }
  } catch (error: unknown) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
}
